import { Close, History } from '@mui/icons-material'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { FC, useEffect, useRef } from 'react'
import { getPxAsRem } from 'shared/utils'
import { RecentSearch } from './types'

interface SearchItemProps {
    search: RecentSearch
    isHighlighted: boolean
    onSelect: (query: string) => void
    onRemove: (query: string) => void
}

const SearchItem: FC<SearchItemProps> = ({
    search,
    isHighlighted,
    onSelect,
    onRemove,
}) => {
    const itemRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isHighlighted && itemRef.current) {
            itemRef.current.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            })
        }
    }, [isHighlighted])

    return (
        <Box
            ref={itemRef}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: getPxAsRem(12),
                padding: getPxAsRem(6, 12),
                cursor: 'pointer',
                borderRadius: getPxAsRem(4),
                backgroundColor: isHighlighted ? 'other.background' : undefined,
                '&:hover': {
                    backgroundColor: 'other.background',
                },
            }}
            onClick={() => onSelect(search.query)}
        >
            <History fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography
                variant="body2"
                flex={1}
                noWrap
                sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                }}
            >
                {search.query}
            </Typography>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation()
                    onRemove(search.query)
                }}
                sx={{
                    opacity: 0.5,
                    '&:hover': { opacity: 1 },
                }}
            >
                <Close fontSize="small" />
            </IconButton>
        </Box>
    )
}

interface RecentSearchesProps {
    searches: RecentSearch[]
    onSelect: (query: string) => void
    onRemove: (query: string) => void
    onClearAll: () => void
    highlightedRow?: number
}

export const RecentSearches: FC<RecentSearchesProps> = ({
    searches,
    onSelect,
    onRemove,
    onClearAll,
    highlightedRow,
}) => {
    const isEmpty = searches.length === 0

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: getPxAsRem(8, 12, 4),
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    Recent Searches
                </Typography>
                {!isEmpty && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        onClick={onClearAll}
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { color: 'text.primary' },
                        }}
                    >
                        Clear
                    </Typography>
                )}
            </Box>
            {isEmpty ? (
                <Box padding={getPxAsRem(8, 12)}>
                    <Typography variant="body2" color="text.disabled">
                        No recent searches
                    </Typography>
                </Box>
            ) : (
                <Stack>
                    {searches.map((search, index) => (
                        <SearchItem
                            key={search.query}
                            search={search}
                            isHighlighted={index === highlightedRow}
                            onSelect={onSelect}
                            onRemove={onRemove}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    )
}
